import { IControl } from "Controls/Interfaces";
import { Custom, Checkbox } from "Controls";
import { ControlEventTypes, GridEventTypes } from "Core/Events/EventTypes";
import { EventArgs } from "Core/Events";
import { CommandService, TextBlockService } from "Service";
import { IFCService } from "IFC/Service";
import { IFilter, IBaseGrid } from "Grids/Interfaces";
import { Navigation, ApplicationParameter, $dms, QueryParameter, App, PageContext, AppContext, Util } from "Core";
import { GridTypes, ImportType } from "Service/Client";

/**
 * Handler of IFC imports.
 * @hidden
 */
export class Import {
    private static filesElement = `<input type="file" name="files" id="files" />`;

    private static importType: IControl;
    private static uploadOnly: Checkbox;
    private static upload: Custom;

    private static filePicker: kendo.ui.Upload;
    private static commandService: CommandService;
    private static textBlockService: TextBlockService;

    private static ifcService: IFCService;
    private static filter: IFilter;
    private static customDmsParameterControl: Custom;

    private static selectedImportType: ImportType;

    public static async initialize(
        importType: IControl,
        upload: Custom,
        uploadOnly: Checkbox,
        commandService: CommandService,
        textBlockService: TextBlockService,
        filter: IFilter,
        customDmsParameterControl: Custom
    ): Promise<void> {
        this.commandService = commandService;
        this.textBlockService = textBlockService;
        this.importType = importType;
        this.filter = filter;
        this.customDmsParameterControl = customDmsParameterControl;
        this.importType.on(ControlEventTypes.Change, (e) => this.importTypeChanged(e));
        $(this.importType.getElement()).attr("name", "ImportTypeID");

        this.ifcService = new IFCService();

        $(this.filter.getElement()).find(".dms-collapse-link").hide();

        this.upload = upload;
        this.uploadOnly = uploadOnly;

        const filePickerPromise = this.initializeFilePicker();

        filter.on(GridEventTypes.ControlDataLoaded, (e) => {
            filePickerPromise.then(() => {
                this.loadImportType();
            });
        });
    }

    private static async initializeFilePicker(): Promise<void> {
        const tb = await this.textBlockService.load(AppContext.current.environment.LanguageCode, "fileupload");
        let files = $(this.filesElement).appendTo(this.upload.getOuterElement());
        let textblocks = tb["fileupload"];
        this.filePicker = new kendo.ui.Upload(files[0], {
            async: {
                saveUrl: "dmsapi/ifcimport/uploadfile",
                removeUrl: "dmsapi/ifcimport/removeFile",
                autoUpload: true,
            },
            multiple: true,
            upload: (e) => this.onUpload(e),
            complete: (e) => this.onUploadComplete(e),
            remove: (e) => this.onRemove(e),
            clear: (e) => this.onRemove(e),
            localization: {
                select: this.textBlockService.getChecked(textblocks, "Select", "fileupload"),
                cancel: this.textBlockService.getChecked(textblocks, "Cancel", "fileupload"),
                remove: this.textBlockService.getChecked(textblocks, "Remove", "fileupload"),
                dropFilesHere: this.textBlockService.getChecked(textblocks, "DropFilesHere", "fileupload"),
                headerStatusUploaded: this.textBlockService.getChecked(textblocks, "StatusUploaded", "fileupload"),
                headerStatusUploading: this.textBlockService.getChecked(textblocks, "StatusUploading", "fileupload"),
                statusFailed: this.textBlockService.getChecked(textblocks, "StatusFailed", "fileupload"),
                statusUploaded: this.textBlockService.getChecked(textblocks, "StatusUploaded", "fileupload"),
                statusUploading: this.textBlockService.getChecked(textblocks, "StatusUploading", "fileupload"),
                invalidFileExtension: this.textBlockService.getChecked(textblocks, "InvalidFileExtension", "fileupload"),
            },
        });
        this.filePicker.wrapper.addClass("dms-control-fileupload");
    }

    private static onRemove(e: kendo.ui.UploadRemoveEvent) {
        let app = <App>(<any>window).app;
        e.data = this.commandService.getContextParameters();
        e.headers = { Authorization: "Bearer " + app.auth.user.access_token };
        PageContext.resizePage();
    }

    private static onUpload(e: kendo.ui.UploadUploadEvent) {
        let app = <App>(<any>window).app;
        let xhr = e.XMLHttpRequest;
        xhr.addEventListener("readystatechange", function (e: any) {
            if (xhr.readyState == 1 /* OPENED */) {
                xhr.setRequestHeader("Authorization", "Bearer " + app.auth.user.access_token);
            }
        });
        e.data = this.commandService.getContextParameters();
    }

    private static onUploadComplete(e: kendo.ui.UploadEvent) {
        PageContext.resizePage();
    }

    private static async loadImportType(): Promise<void> {
        let importTypeId = Number(this.importType.getValue());
        if (importTypeId == 0) {
            return;
        }
        this.filter.setBusy(true);
        try {
            this.selectedImportType = await this.ifcService.getImportType(importTypeId);
            this.filePicker.enable(this.selectedImportType.FileNeeded);

            let formContainer = $(this.filter.getElement()).find(".dms-form-content");

            formContainer.find(".dms-ifcparameter-row").remove();
            this.customDmsParameterControl.disposeMapping(true);

            for (const parameter of this.selectedImportType.Parameters) {
                let parameterRow = $("<div class='dms-form-row row dms-ifcparameter-row'/>");
                let column = $(`<div class="dms-column dms-column-form col-sm" />`).appendTo(parameterRow);
                let formGroup = $(`<div class="form-group row" />`).appendTo(column);

                const description = parameter.Description || `<span class="dms-missing-textblock">${parameter.ParameterName}</span>`;

                $(
                    `<label class="dms-form-label col-sm-4 col-form-label dms-tooltip" data-dms-tooltip="${parameter.Description}">${description}</label>`
                ).appendTo(formGroup);

                let parameterValue = parameter.ParameterValue;
                if (QueryParameter.get(parameter.ParameterName) != null) {
                    parameterValue = QueryParameter.get(parameter.ParameterName);
                }
                let controlColumn = $(`<div class="dms-column col-sm-8">`).appendTo(formGroup);
                $(
                    `<input type="text" name="${parameter.ParameterName}" value="${parameterValue}" class="dms-ifc-parameter k-input k-textbox k-input-solid k-input-md k-rounded-md dms-control-input dms-control dms-control-text" />`
                ).appendTo(controlColumn);

                formContainer.append(parameterRow);
            }

            if (!Util.stringIsNullOrEmpty(this.selectedImportType.ParameterDMSMappingName)) {
                await this.customDmsParameterControl.initializeMapping(this.selectedImportType.ParameterDMSMappingName);
                formContainer.find(".dms-collapse-link").hide();
            }
        } finally {
            this.filter.setBusy(false);
            PageContext.resizePage();
        }
    }

    private static importTypeChanged(e: EventArgs<IControl>) {
        console.log("changed");
        this.loadImportType();
        this.removeAllFiles();
    }

    private static removeAllFiles() {
        this.filePicker.wrapper.find(".k-upload-action").click();
    }

    public static import(): Promise<boolean> {
        if (this.selectedImportType == null) {
            return;
        }
        let importTypeId = this.selectedImportType.ImportTypeID;

        if (this.selectedImportType.FileNeeded && this.filePicker.getFiles().length == 0) {
            Navigation.errorAsync(this.textBlockService.get("noFile", "ifc_import"), "Import");
            return;
        }

        this.filter.setBusy(true);

        let parameters = $(this.filter.getElement()).find(".dms-ifc-parameter");
        let parameterValues: { [key: string]: string } = {};
        for (let i = 0, len = parameters.length; i < len; i++) {
            let param = $(parameters[i]);
            parameterValues[param.attr("name")] = param.val() as string;
        }

        const mapping = this.customDmsParameterControl.getMapping();
        if (mapping) {
            const grids = mapping.getGrids();
            for (const grid of grids) {
                if (grid.getGridType() == GridTypes.Filter) {
                    const filter = <IFilter>grid;
                    const filterValues = { ...filter.getData() };
                    Util.__sanitizeDataRow(filterValues);
                    parameterValues = <any>{ ...parameterValues, ...filterValues };
                }
            }
        }

        const uploadOnly = this.uploadOnly != null ? this.uploadOnly.getValue() : false;

        (<IBaseGrid>$dms("ifc_Message")).hide();
        return this.ifcService.import(importTypeId, uploadOnly, parameterValues).then((res) => {
            ApplicationParameter.set("IESessionGUID", res.IESessionGUID);
            (<IBaseGrid>$dms("ifc_Message")).reload();
            this.filter.setBusy(false);
            this.removeAllFiles();
            PageContext.resizePage();

            if (res.ErrorCount) {
                Navigation.errorAsync(res.MessageString, "Error");
                return false;
            } else if (res.ResultTextBlockName) {
                ApplicationParameter.set("linesTotal", res.LinesTotal || 0);
                ApplicationParameter.set("linesOK", res.LinesImported || 0);
                ApplicationParameter.set("errorCount", res.ErrorCount || 0);
                ApplicationParameter.set("linesError", res.LinesWithError || 0);
                ApplicationParameter.set("linesErrorCount", res.LinesErrorCount || 0);
                let text = this.textBlockService.get(res.ResultTextBlockName, "ifc_import");
                Navigation.errorAsync(text, "Import");
                return true;
            }

            setTimeout(() => {
                PageContext.resizePage();
            }, 100);
        });
    }
}
